import React, { useState } from 'react';
import { FlatList, Image, TouchableOpacity, View } from "react-native"
import type { HedgeProps, LeagueProps, OrderProps, OrderStatsProps } from '../../types';
import { Button, Icons, Text } from '../../Components';
import Colors from '../../constants/colors';
import { view_styles } from '../../constants/styles';
import ConfirmationModal from '../../Components/ConfirmationModal';
import { MarketComponentApi } from '../api';

type ContestOrderStatsCardProps = {
    player_order_stat: OrderStatsProps,
    contest_title:string,
    league?:LeagueProps,
    hedges:HedgeProps[],
    view_mode?:'desktop'|'mobile',
    market_type:'FOR_MONEY'|'FREE',
    contest_time_detail:string,
    onPress: (player_order_stat:OrderStatsProps) => void,
    onOrdersUpdate: (orders:OrderProps[]) => void
}

const ContestOrderStatsCard = ({ player_order_stat, view_mode, league, market_type, contest_time_detail, hedges, contest_title, onPress, onOrdersUpdate }:ContestOrderStatsCardProps) => {
    const [ confirm_data, setConfirmData ] = useState<{
        visible:boolean,
        title?:string,
        description?:string,
        confirm_title?:string,
        cancel_title?:string
    }>({
        visible:false
    });
    const [ action_loading, setActionLoading ] = useState(false);
    const [ show_titles, setShowTitles ] = useState(false);

    const handleCardPress = () => {
        if(!onPress){ return }
        onPress(player_order_stat)
    }

    const renderTitles = (data:{item:string, index:number}) => {
        return (
            <View style={{ ...view_styles.body_row, padding:10, borderBottomWidth:1, borderColor:Colors.shades.shade600 }}>
                <Text size={14} color={Colors.brand.midnight} weight='semibold'>{data.item}</Text>
            </View>
        )
    }

    const handleCancelOrders = async(order_ids:string[]) => {
        setActionLoading(true);
        const orders = await MarketComponentApi.cancelOrders(order_ids)
        setActionLoading(false);
        onOrdersUpdate(orders);
    }

    const cl = market_type == 'FOR_MONEY' ? '$' : 'E'
    const hedged_amt = hedges.reduce((a,b) => a + b.cash_rcvd, 0)

    return (
        <View>
            <View style={{ ...view_styles.section, margin:5 }}>
                <TouchableOpacity style={{ ...view_styles.section_header }} onPress={() => handleCardPress()}>
                    {league ?
                    <Image
                        source={{ uri: league.league_image }}
                        style={{ height:25, width:25 }}
                        resizeMode='cover'
                    />
                    :<></>}
                    <View style={{ flex:1, marginLeft:10, marginRight:10 }}>
                        <Text theme='header_2'>{contest_title}</Text>
                        <Text theme='body_2' style={{ marginTop:3 }}>{contest_time_detail}</Text>
                    </View>
                    <Icons.ChevronIcon direction='right' color={Colors.brand.midnight} size={12} />
                </TouchableOpacity>
                <View style={{ ...view_styles.section_body }}>
                    <View style={{ flexDirection:'row', flexWrap:'wrap'}}>
                        {player_order_stat.open_amt > 0 ?
                        <View nativeID='open_orders_lozenge' style={{ borderRadius:100, backgroundColor:Colors.shades.shade100, padding:5, paddingRight:10, paddingLeft:10 }}>
                            <Text size={12} weight='semibold' color={Colors.brand.cyan}>OPEN: {cl}{player_order_stat.open_amt.toFixed(2)}</Text>
                        </View>
                        :<></>}
                        {player_order_stat.unresolved_stake + player_order_stat.resolved_stake > 0 ?
                        <View nativeID='stake_lozenge' style={{ borderRadius:100, backgroundColor:Colors.shades.shade100, padding:5, paddingRight:10, paddingLeft:10 }}>
                            <Text size={12} weight='semibold' color={Colors.brand.midnight}>POSITION: {cl}{(player_order_stat.unresolved_stake + player_order_stat.resolved_stake).toFixed(2)}</Text>
                        </View>
                        :<></>}
                        {hedges.length > 0 && hedged_amt > 0 ?
                        <View nativeID='stake_lozenge' style={{ borderRadius:100, backgroundColor:Colors.highlights.highlight400Faded, padding:5, paddingRight:10, paddingLeft:10 }}>
                            <Text size={12} weight='semibold' color={Colors.brand.midnight}>HEDGED: {cl}{hedged_amt.toFixed(2)}</Text>
                        </View>
                        :<></>}
                        {player_order_stat.resolved_stake > 0 ?
                        <View nativeID='stake_lozenge' style={{ borderRadius:100, backgroundColor:Colors.shades.shade100, padding:5, paddingRight:10, paddingLeft:10 }}>
                            <Text size={12} weight='semibold' color={Colors.brand.midnight}>EARNED: {cl}{player_order_stat.earnings.toFixed(2)}</Text>
                        </View>
                        :<></>}
                    </View>
                </View>
                {show_titles && player_order_stat.unique_order_titles ?
                <View style={{ ...view_styles.section_body, padding:10, borderTopWidth:1, borderColor:Colors.shades.shade600 }}>
                    <FlatList
                        data={player_order_stat.unique_order_titles}
                        renderItem={renderTitles}
                        keyExtractor={(item) => item}
                    />
                </View>
                :<></>}
                <View style={{ ...view_styles.section_footer, padding:10 }}>
                    <TouchableOpacity style={{ flexDirection:'row', flex:2, alignItems:'center' }} onPress={() => setShowTitles(!show_titles)}>
                        <View style={{ height:30, width:30, borderRadius:100, backgroundColor:Colors.shades.white, justifyContent:'center', alignItems:'center', ...view_styles.float }}>
                            <Icons.ChevronIcon direction={show_titles ? 'up' : 'down'} size={6} color={Colors.brand.electric} />
                        </View>
                        <Text size={14} color={Colors.brand.electric} style={{ marginLeft:5 }}>{show_titles?'Hide':'Show'} Unique Orders</Text>
                    </TouchableOpacity>
                    {player_order_stat.open_amt > 0 ?
                    <Button
                        title='CANCEL ORDERS'
                        title_color={Colors.shades.white}
                        style={{ opacity:action_loading ? 0.5 : 1, flexShrink:1 }}
                        loading={action_loading}
                        disabled={action_loading}
                        backgroundColor={Colors.utility.error}
                        onPress={async() => {
                            setConfirmData({
                                visible: true,
                                title: 'Are you sure?',
                                confirm_title:'CANCEL ALL',
                                cancel_title: 'KEEP ALL',
                                description: `Press cancel all to clear the remaining $${player_order_stat.open_amt.toFixed(2)} that is open on this event`
                            });
                        }}
                    />
                    :<></>}
                    <View style={{ position:'absolute' }}>
                        <ConfirmationModal
                            visible={confirm_data.visible}
                            title={confirm_data.title}
                            mode={view_mode ?? 'mobile'}
                            description={confirm_data.description}
                            confirm_title={confirm_data.confirm_title}
                            cancel_title={confirm_data.cancel_title}
                            onCancel={() => setConfirmData({ visible:false })}
                            onConfirm={() => {
                                setConfirmData({ visible:false });
                                handleCancelOrders(player_order_stat.open_orders)
                            }}
                        />
                    </View>
                </View>
            </View>
        </View>
       
    )
}

export default ContestOrderStatsCard